import { ApolloError } from 'apollo-boost';
import ErrorsShower from 'core/components/common/ErrorsShower';
import { DEFAULT_SOON_CAUSE } from 'core/const';
import { column, container, title } from 'core/style';
import injectSheet, { createStyles } from 'core/style/injectSheet';
import { ITheme, WithStyles } from 'core/style/interfaces';
import { formatToLocalDayMonth, formatToLocalTime, formatToLocalDayMonthYear, isImpossibleDate } from 'core/utils/date';
import { isFilledArray } from 'core/utils/fp';
import {
    filterCertificates,
    filterMaterials,
    filterRecords,
    filterVideoCourses,
    nearestSeminarsAndStarted,
    nearestWebinarsAndStarted,
} from 'core/utils/product-forms/filterProductForms';
import { orderEventRecordsDesc, orderSeminars, orderWebinars } from 'core/utils/product-forms/orderProductForms';
import { isDatePast } from 'core/utils/product-forms/productForms';
import {
    selectCertificates,
    selectMaterials,
    selectRecords,
    selectVideoCourses,
} from 'core/utils/product-forms/selectProductForms';
import React from 'react';
import { GetPrivateProductDetailDataQuery, ProductFormPublicTypeEnum, Seminar, Webinar } from 'site/graphql';
import {
    ExpandCardsCertificate,
    ExpandCardsEventRecord,
    ExpandCardsMaterial,
    ExpandCardsSeminar,
    ExpandCardsVideoCourse,
    ExpandCardsWebinar,
} from './expands';
import PrivateProductCertificateCard from './form-item-cards/PrivateProductCertificateCard';
import PrivateProductEventRecordCard from './form-item-cards/PrivateProductEventRecordCard';
import PrivateProductMaterialCard from './form-item-cards/PrivateProductMaterialCard';
import PrivateProductSeminarCard from './form-item-cards/PrivateProductSeminarCard';
import PrivateProductVideoCourseCard from './form-item-cards/PrivateProductVideoCourseCard';
import PrivateProductWebinarCard from './form-item-cards/PrivateProductWebinarCard';
import PrivateProductDetailFilter from './PrivateProductDetailFilter';
import PrivateProductDetailHeader from './PrivateProductDetailHeader';

const nearestWebinarsItemsAndStartedWithSetDay = <T extends Pick<Webinar, 'date' | 'hours'>>(webinars: T[]) =>
    orderWebinars(webinars)
        .map((webinar, index) => ({ ...webinar, day: index + 1 }))
        .filter(webinar => !isDatePast(webinar.date, webinar.hours));

const nearestSeminarsItemsAndStartedWithSetDay = <T extends Pick<Seminar, 'date' | 'hours'>>(seminars: T[]) =>
    orderSeminars(seminars)
        .map((Seminar, index) => ({ ...Seminar, day: index + 1 }))
        .filter(Seminar => !isDatePast(Seminar.date, Seminar.hours));

type IPrivateProductDetailProps = {
    product: GetPrivateProductDetailDataQuery['purchasedProduct'];
    loading: boolean;
    error: ApolloError;
    productId: number;
    hasSubscription: boolean;
} & WithStyles<typeof styles>;

interface IPrivateProductDetailState {
    filterResult: 'DEFAULT' | ProductFormPublicTypeEnum;
}

class PrivateProductDetail extends React.PureComponent<IPrivateProductDetailProps, IPrivateProductDetailState> {
    static defaultProps: any = {
        values: {
            id: 0,
            lecturers: [],
            productForms: [],
        },
        account: {},
    };

    state: IPrivateProductDetailState = {
        filterResult: 'DEFAULT',
    };

    changeFilter = (formType: ProductFormPublicTypeEnum | 'DEFAULT') => this.setState({ filterResult: formType });

    renderNoAccess() {
        const { classes } = this.props;

        return (
            <p className={classes.noAccessText}>
                Доступ предоставляется при условии покупки курсов Красного Циркуля в текущем календарном году
            </p>
        );
    }

    renderWebinars() {
        const { classes, product } = this.props;
        const { productForms = [] } = product;
        const webinarForms = nearestWebinarsAndStarted(productForms);

        return (
            isFilledArray(webinarForms) && (
                <>
                    <h2 className={classes.content__title}>Онлайн занятия</h2>
                    {webinarForms.map(form => {
                        const selectedFormItems = nearestWebinarsItemsAndStartedWithSetDay(form.webinars);

                        return (
                            <React.Fragment key={form.id}>
                                <h3 className={classes.content__subtitle}>{form.title}</h3>
                                {form.order.accessUntil && (
                                    <div className={classes.content__accessUntil}>
                                        Доступ открыт до {formatToLocalDayMonthYear(form.order.accessUntil)}
                                    </div>
                                )}
                                <ExpandCardsWebinar
                                    collapseCount={3}
                                    items={selectedFormItems}
                                    classes={{ root: classes.content__forms, button: classes.content__button }}
                                    linkTitle="Показать все онлайн занятия по курсу"
                                >
                                    {webinar => (
                                        <div key={webinar.id} className={classes.forms__item}>
                                            <div className={classes.item__time}>
                                                {isImpossibleDate(webinar.date)
                                                    ? webinar.soonCause || DEFAULT_SOON_CAUSE
                                                    : `${formatToLocalDayMonth(webinar.date)}, в ${formatToLocalTime(
                                                          webinar.date,
                                                      )}`}
                                            </div>
                                            <PrivateProductWebinarCard webinar={webinar} />
                                        </div>
                                    )}
                                </ExpandCardsWebinar>
                            </React.Fragment>
                        );
                    })}
                </>
            )
        );
    }

    renderSeminars() {
        const { classes, product } = this.props;
        const { productForms = [] } = product;
        const seminarForms = nearestSeminarsAndStarted(productForms);

        return (
            isFilledArray(seminarForms) && (
                <>
                    <h2 className={classes.content__title}>Семинары</h2>
                    {seminarForms.map(form => (
                        <React.Fragment key={form.id}>
                            <h3 className={classes.content__subtitle}>{form.title}</h3>
                            {form.order.accessUntil && (
                                <div className={classes.content__accessUntil}>
                                    Доступ открыт до {formatToLocalDayMonthYear(form.order.accessUntil)}
                                </div>
                            )}
                            <ExpandCardsSeminar
                                collapseCount={3}
                                items={nearestSeminarsItemsAndStartedWithSetDay(form.seminars)}
                                classes={{ root: classes.content__forms, button: classes.content__button }}
                                linkTitle="Показать все семинары по курсу"
                            >
                                {seminar => (
                                    <div key={seminar.id} className={classes.forms__item}>
                                        <div className={classes.item__time}>
                                            {isImpossibleDate(seminar.date)
                                                ? DEFAULT_SOON_CAUSE
                                                : `${formatToLocalDayMonth(seminar.date)}, в ${formatToLocalTime(
                                                      seminar.date,
                                                  )}`}
                                        </div>
                                        <PrivateProductSeminarCard
                                            title={seminar.title}
                                            description={seminar.description}
                                            hours={seminar.hours}
                                            lecturers={seminar.lecturers}
                                            record={seminar.eventRecord}
                                            materials={seminar.materials}
                                        />
                                    </div>
                                )}
                            </ExpandCardsSeminar>
                        </React.Fragment>
                    ))}
                </>
            )
        );
    }

    renderVideoCourses() {
        const { classes, product, hasSubscription } = this.props;
        const { productForms = [] } = product;
        const videoCoursesForms = filterVideoCourses(productForms);

        return (
            videoCoursesForms.length > 0 && (
                <>
                    <h2 className={classes.content__title}>Видеокурс</h2>
                    {videoCoursesForms.map(form => {
                        const noAccess = form.subscriptionAccess && !hasSubscription;

                        return (
                            <React.Fragment key={form.id}>
                                <h3 className={classes.content__subtitle}>{form.title}</h3>
                                {form.order.accessUntil && (
                                    <div className={classes.content__accessUntil}>
                                        Доступ открыт до {formatToLocalDayMonthYear(form.order.accessUntil)}
                                    </div>
                                )}
                                {noAccess && this.renderNoAccess()}
                                <ExpandCardsVideoCourse
                                    collapseCount={3}
                                    items={selectVideoCourses(form)}
                                    classes={{ root: classes.content__forms, button: classes.content__button }}
                                    linkTitle="Показать все видеокурсы"
                                >
                                    {videoCourse => (
                                        <div key={videoCourse.id} className={classes.forms__item}>
                                            <PrivateProductVideoCourseCard
                                                videoCourse={videoCourse}
                                                noAccess={noAccess}
                                            />
                                        </div>
                                    )}
                                </ExpandCardsVideoCourse>
                            </React.Fragment>
                        );
                    })}
                </>
            )
        );
    }

    renderEventRecords() {
        const { classes, product, hasSubscription } = this.props;
        const { productForms = [] } = product;
        const recordsForms = filterRecords(productForms).filter(pf => selectRecords(pf).length > 0);

        return (
            recordsForms.length > 0 && (
                <>
                    <h2 className={classes.content__title}>Видеозаписи</h2>
                    {recordsForms.map(form => {
                        const noAccess = form.subscriptionAccess && !hasSubscription;

                        return (
                            <React.Fragment key={form.id}>
                                <h3 className={classes.content__subtitle}>{form.title}</h3>
                                {form.order.accessUntil && (
                                    <div className={classes.content__accessUntil}>
                                        Доступ открыт до {formatToLocalDayMonthYear(form.order.accessUntil)}
                                    </div>
                                )}
                                {noAccess && this.renderNoAccess()}
                                <ExpandCardsEventRecord
                                    collapseCount={3}
                                    items={orderEventRecordsDesc(selectRecords(form))}
                                    classes={{ root: classes.content__forms, button: classes.content__button }}
                                    linkTitle="Показать все видеозаписи по курсу"
                                >
                                    {eventRecord => (
                                        <div key={eventRecord.id} className={classes.forms__item}>
                                            <PrivateProductEventRecordCard
                                                eventRecord={eventRecord}
                                                noAccess={noAccess}
                                            />
                                        </div>
                                    )}
                                </ExpandCardsEventRecord>
                            </React.Fragment>
                        );
                    })}
                </>
            )
        );
    }

    renderMaterials() {
        const { classes, product, hasSubscription } = this.props;
        const { productForms = [] } = product;
        const materialsForms = filterMaterials(productForms).filter(pf => selectMaterials(pf).length > 0);

        return (
            materialsForms.length > 0 && (
                <>
                    <h2 className={classes.content__title}>Материалы</h2>
                    {materialsForms.map(form => {
                        const noAccess = form.subscriptionAccess && !hasSubscription;

                        return (
                            <React.Fragment key={form.id}>
                                <h3 className={classes.content__subtitle}>{form.title}</h3>
                                {form.order.accessUntil && (
                                    <div className={classes.content__accessUntil}>
                                        Доступ открыт до {formatToLocalDayMonthYear(form.order.accessUntil)}
                                    </div>
                                )}
                                {noAccess && this.renderNoAccess()}
                                <ExpandCardsMaterial
                                    collapseCount={3}
                                    items={selectMaterials(form)}
                                    classes={{ root: classes.content__forms, button: classes.content__button }}
                                    linkTitle="Показать все материалы по курсу"
                                >
                                    {material => (
                                        <div key={material.id} className={classes.forms__item}>
                                            <PrivateProductMaterialCard material={material} noAccess={noAccess} />
                                        </div>
                                    )}
                                </ExpandCardsMaterial>
                            </React.Fragment>
                        );
                    })}
                </>
            )
        );
    }

    renderCertificates() {
        const { classes, product, hasSubscription } = this.props;
        const { productForms = [] } = product;
        const certificateForms = filterCertificates(productForms);

        return (
            certificateForms.length > 0 && (
                <>
                    <h2 className={classes.content__title}>Сертификаты</h2>
                    {certificateForms.map(form => {
                        const noAccess = form.subscriptionAccess && !hasSubscription;

                        return (
                            <React.Fragment key={form.id}>
                                <h3 className={classes.content__subtitle}>{form.title}</h3>
                                {form.order.accessUntil && (
                                    <div className={classes.content__accessUntil}>
                                        Доступ открыт до {formatToLocalDayMonthYear(form.order.accessUntil)}
                                    </div>
                                )}
                                {noAccess && this.renderNoAccess()}
                                <ExpandCardsCertificate
                                    collapseCount={3}
                                    items={selectCertificates(form)}
                                    classes={{ root: classes.content__forms, button: classes.content__button }}
                                    linkTitle="Показать все сертификаты по курсу"
                                >
                                    {certificate => (
                                        <div key={certificate.id} className={classes.forms__item}>
                                            <PrivateProductCertificateCard
                                                certificate={certificate}
                                                noAccess={noAccess}
                                            />
                                        </div>
                                    )}
                                </ExpandCardsCertificate>
                            </React.Fragment>
                        );
                    })}
                </>
            )
        );
    }

    renderContent() {
        switch (this.state.filterResult) {
            case ProductFormPublicTypeEnum.Webinar:
                return this.renderWebinars();
            case ProductFormPublicTypeEnum.Seminar:
                return this.renderSeminars();
            case ProductFormPublicTypeEnum.VideoCourse:
                return this.renderVideoCourses();
            case ProductFormPublicTypeEnum.EventRecord:
                return this.renderEventRecords();
            case ProductFormPublicTypeEnum.Material:
                return this.renderMaterials();
            case ProductFormPublicTypeEnum.Certificate:
                return this.renderCertificates();
            default:
                return (
                    <>
                        {this.renderWebinars()}
                        {this.renderSeminars()}
                        {this.renderVideoCourses()}
                        {this.renderEventRecords()}
                        {this.renderMaterials()}
                        {this.renderCertificates()}
                    </>
                );
        }
    }

    render() {
        const { classes, product, error, loading, productId } = this.props;
        const { productForms = [] } = product;
        const formTypes = productForms.map(pf => pf.type);

        return (
            <ErrorsShower loading={loading} error={error}>
                <div className={classes.root}>
                    <PrivateProductDetailHeader productId={productId} product={product} />
                    <div className={classes.detail__container}>
                        <div className={classes.detail__content}>
                            <PrivateProductDetailFilter
                                forms={formTypes}
                                changeFilter={this.changeFilter}
                                displayWebinarButton={isFilledArray(nearestWebinarsAndStarted(productForms))}
                                displaySeminarButton={isFilledArray(nearestSeminarsAndStarted(productForms))}
                            />
                            {this.renderContent()}
                        </div>
                    </div>
                </div>
            </ErrorsShower>
        );
    }
}

const styles = (theme: ITheme) =>
    createStyles({
        root: {},
        detail__container: {
            ...container(theme, { gutter: 30 }),
        },
        detail__content: {
            ...column(),
            paddingLeft: 0,
            paddingRight: 0,
        },
        content: {},
        content__title: {
            ...title({ mode: 'left', size: 'h2' }),

            '&:first-of-type': {
                marginTop: 1.75 + 'rem',
            },
        },
        content__subtitle: {
            ...title({ mode: 'left', size: 'h3' }),
            marginTop: '1.75rem',
        },

        content__accessUntil: {
            color: theme.colors.error,
            margin: '1rem 0',
        },
        content__forms: {
            display: 'flex',
            flexDirection: 'row',
            flexWrap: 'wrap',
            margin: '0 -30px',
        },
        content__button: {
            marginLeft: 30,
            marginBottom: 30,

            [theme.breakpoints.down('xs')]: {
                marginLeft: 'auto',
            },
        },
        forms: {},
        forms__item: {
            padding: '0 30px',
            marginBottom: 30,
            display: 'flex',
            flexDirection: 'column',
            flex: '1 1 100%',
            boxSizing: 'border-box',
            [theme.breakpoints.up('sm')]: {
                flexBasis: '50%',
                maxWidth: '50%',
            },
            [theme.breakpoints.up('md')]: {
                flexBasis: '33.333%',
                maxWidth: '33.333%',
            },
        },
        item: {},
        item__num: {
            color: theme.colors.accent,
            textAlign: 'center',
            fontWeight: 'bold',
        },
        item__time: {
            color: '#29323b',
            textAlign: 'center',
            fontWeight: 'bold',
        },
        link: {},
        link__icon: {
            width: 24,
            height: 24,
            marginLeft: 5,
            verticalAlign: 'middle',
            fill: theme.colors.accent,
        },
        link__text: {
            textDecoration: 'none',
            color: theme.colors.accent,
            fontWeight: 'bold',
        },
        noAccessText: {
            margin: 0,
            fontSize: 14,
            color: '#808080',
        },
    });

export default injectSheet(styles, 'PrivateProductDetail')(PrivateProductDetail);
